import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Stack,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  IconButton,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  Checkbox,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  ListItemButton,
} from "@mui/material";
import { ReactComponent as ToolsIcon } from "../static/Icons/tools.svg";
import { ReactComponent as ActionsIcon } from "../static/Icons/actions.svg";
import { ReactComponent as TestIcon } from "../static/Icons/test.svg";
import { ReactComponent as ConnectIcon } from "../static/Icons/connect.svg";
import { ReactComponent as HistoryIcon } from "../static/Icons/history.svg";
import { ReactComponent as FilterIcon } from "../static/Icons/filter-search.svg";
import { ReactComponent as RefreshIcon } from "../static/Icons/rotate-right.svg";
import { ReactComponent as Google } from "../static/Icons/google.svg";
import { ReactComponent as BackIcon } from "../static/Icons/arrow-left.svg";
import Add from "@mui/icons-material/Add";
import { RightSideContainer } from "./DashboardLayout";
import ActionPage from "./ActionPage";
import TestPage from "./TestPage";
import ConnectPage from "./ConnectPage";
import ButtonCommon from "../CommonComponents/ButtonCommon";
import CommonModal from "../CommonComponents/CommonModal";
import SearchIcon from "@mui/icons-material/Search";
import HistoryPage from "./HistoryPage";
import ToolsPage from "./ToolsPage";
import Logo from "../static/Icons/mcp-logo.svg";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { connectTool, getAllTools, getToolById } from "../api/tool";
import { useLocation, useParams } from "react-router-dom";
import { addActionInServer, getServerById } from "../api/server";
import { showErrorToast, showSuccessToast } from "../toastUtils";

const ServerDetails = () => {
  const { divHeight } = useContext(RightSideContainer);
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [serverDetails, setServerDetails] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tabIsClicked, setTabIsClicked] = useState(false);
  const [filterValue, setFilterValue] = useState(null);
  const [initialFilterValue, setInitialFilterValue] = useState(null);
  const [searchFilter, setSearchFilter] = useState(null);

  // For add tool button modal states
  const [openModal, setOpenModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [finalConnectModal, setFinalConnectModal] = useState(false);
  
  const [isAuthCompleted, setIsAuthCompleted] = useState(false);
  const [oauthMethod, setOauthMethod] = useState("");
  const [personalAccessToken, setPersonalAccessToken] = useState("");
  const [tokenError, setTokenError] = useState("");

  // For new add tool modal
  const [allTools, setAllTools] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [applicationActions, setApplicationActions] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [actionsSearch, setActionSearch] = useState("");
  const [loadingTools, setLoadingTools] = useState(false);
  const [loadingApplicationActions, setLoadingApplicationActions] =
    useState(false);

  // For components
  const [toolActionData, setToolActionData] = useState([]);
  const [isBulkAdd, setIsBulkAdd] = useState(false); // for checking if selected all actions or not
  const oauthStatus = sessionStorage.getItem("toolAuth");

  const tabData = [
    { label: "Tools", icon: ToolsIcon },
    { label: "Actions", icon: ActionsIcon },
    { label: "Test", icon: TestIcon },
    { label: "Connect", icon: ConnectIcon },
    { label: "History", icon: HistoryIcon },
  ];

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setTabIsClicked(true);
  };

  const formatLabel = (text) => {
    if (!text) return "";
    return text
      .split("_")
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    if (location.state?.reopenFinalConnectModal) {
      const stored = sessionStorage.getItem("postOAuthModalState");
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log("Restoring OAuth modal state:", parsed);
        // Validate that we have the required data
        if (!parsed.tool) {
          console.error("Missing tool in stored OAuth state");
          return;
        }
        if (!parsed.selectedActions || parsed.selectedActions.length === 0) {
          console.error(
            "Missing or empty selectedActions in stored OAuth state"
          );
          return;
        }

        setSelectedTool(parsed.tool);
        setSelectedActions(parsed.selectedActions || []);
        setIsBulkAdd(parsed.isBulkAdd || false);
        setFinalConnectModal(true);
        if (oauthStatus === "true") {
          setIsAuthCompleted(true);
        }
        // Cleanup
        sessionStorage.removeItem("postOAuthModalState");
        
        // Refresh server details to get updated toolAuth status
        const refreshServerData = async () => {
          try {
            const res = await getServerById(id);
            if (res.success) {
              setServerDetails(res.data);
            }
          } catch (error) {
            console.error("Error refreshing server data after OAuth:", error);
          }
        };
        refreshServerData();
      }
    }
    //eslint-disable-next-line
  }, [location.state, id]);

  useEffect(() => {
    if (selectedTab === 1 && serverDetails?.actions) {
      const selectedToolIds = [];
      const selectedActionIds = [];
      const allActions = [];

      serverDetails.actions.forEach((tool) => {
        selectedToolIds.push(tool.toolId);

        tool.toolActions.forEach((toolAction) => {
          selectedActionIds.push(toolAction.actionId);
          allActions.push({
            ...toolAction,
            toolAuth: tool.toolAuth === "true",
            toolIcon: tool.toolIcon,
            toolName: tool.toolName,
            authOptions: tool.authOptions,
          });
        });
      });

      // ✅ Set filterValue with arrays once
      setFilterValue({
        tool: selectedToolIds,
        action: selectedActionIds,
      });

      // Set initialFilterValue with arrays once
      setInitialFilterValue({
        tool: selectedToolIds,
        action: selectedActionIds,
      });

      // ✅ Set toolActions once
      setToolActionData({ toolActions: allActions });

      console.log("serverDetails", serverDetails);
      setTabIsClicked(false);
    }
  }, [selectedTab, serverDetails, tabIsClicked]);

  useEffect(() => {
    async function fetchServerDetails() {
      if (id) {
        try {
          setLoading(true);
          const res = await getServerById(id);
          if (res.success) {
            setServerDetails(res.data);
          } else {
            showErrorToast(res.message);
          }
        } catch (error) {
          console.error("Error fetching server details:", error);
        } finally {
          setLoading(false);
        }
      }
    }

    fetchServerDetails();
  }, [id]);

  const handleSelectApplication = async (tool) => {
    setSelectedApplication(tool);
    setLoadingApplicationActions(true);
    setApplicationActions([]);
    setSelectedActions([]); // Reset selected actions when app changes
    try {
      const res = await getToolById(tool.toolId);
      if (res.success && res.data) {
        setApplicationActions(res.data.toolActions || []);
        setSelectedTool(res.data);
      } else {
        setApplicationActions([]);
        showErrorToast(res.message || "Data not found");
      }
    } catch (error) {
      setApplicationActions([]);
      showErrorToast("Error fetching application actions");
    } finally {
      setLoadingApplicationActions(false);
    }
  };

  const handleToggleActionSelection = (action) => {
    setSelectedActions((prev) => {
      // Debug: Log to see what's being passed
      console.log("Action:", action);
      console.log("Current selected:", prev);

      // Use actionName instead of actionId
      if (!action || !action.actionName) {
        console.error("Invalid action or missing actionName:", action);
        return prev;
      }

      // Use actionName for comparison
      const isSelected = prev.some((a) => a.actionName === action.actionName);

      if (isSelected) {
        // Remove the action
        return prev.filter((a) => a.actionName !== action.actionName);
      } else {
        // Add the action (create new object to avoid reference issues)
        return [...prev, { ...action }];
      }
    });
  };

  const handleChange = (event) => {
    const selectedMethod = event.target.value;
    setOauthMethod(selectedMethod);

    // If not personal token, call API immediately
    if (selectedMethod !== "personal_access_token") {
      handleToolConnect(selectedMethod, selectedTool);
    }
  };

  const handleTokenSubmit = () => {
    if (!personalAccessToken.trim()) {
      setTokenError("Access token is required");
      return;
    }
    setTokenError("");
    handleToolConnect("personal_access_token", selectedTool);
  };

  useEffect(() => {
    const fetchAllTools = async () => {
      if (openModal) {
        setLoadingTools(true);
        try {
          const res = await getAllTools();
          if (res.success) {
            setAllTools(res.data);
          } else {
            showErrorToast(res.message);
          }
        } catch (error) {
          showErrorToast("Error fetching tools");
        } finally {
          setLoadingTools(false);
        }
      }
    };
    fetchAllTools();
  }, [openModal]);

  const generateActionPayload = (selectedTool, action, authStatus) => {
    const actionNeedsArray = (action.actionNeeds || []).map((need) => ({
      type: need.type,
      value: "$$$GUESS$$$",
      required: need.required,
      param: need.param,
      description: need.description,
    }));

    return {
      toolId: selectedTool.toolId,
      toolName: selectedTool.toolName,
      toolDescription: selectedTool.toolDescription,
      toolIcon: selectedTool.toolIcon,
      toolActions: [
        {
          actionName: action.actionName,
          actionDisplayName: action.actionDisplayName,
          actionDescription: action.actionDescription,
          actionType: action.actionType,
          actionKey: action.actionKey,
          actionNeeds: actionNeedsArray,
          isHidden: action.isHidden,
        },
      ],
      toolAuth: authStatus,
      authOptions: selectedTool.authOptions,
    };
  };

  const handleToolConnect = async (method, provider) => {
    setLoading(true);
    try {
      const payload = {
        provider: provider?.toolName,
        scopes: Array.isArray(provider?.authScope)
          ? provider.authScope.join(",")
          : "",
        redirect: `${window.location.origin}/dashboard/oauth/callback`,
        auth_method: method,
      };

      if (method === "personal_access_token") {
        payload.access_token = personalAccessToken;
      }

      const res = await connectTool(payload);

      if (method === "personal_access_token") {
        // For Personal Access Token authentication
        if (res?.success) {
          setLoading(false);
          setIsAuthCompleted(true);
          sessionStorage.setItem("toolAuth", String(true));
          showSuccessToast("Personal access token authenticated successfully!");
          await handleSave();
        } else {
          setLoading(false);
          showErrorToast(res?.message || "Authentication failed");
        }
      } else if (res?.data?.auth_url) {
        // For OAuth authentication
        setLoading(false);
        localStorage.setItem("oauthResponse", JSON.stringify(res));
        const oauthState = {
          tool: selectedTool,
          selectedActions,
          isBulkAdd,
          returnTo: `/dashboard/server/${id}`,
        };
        console.log("Storing OAuth state before redirect:", oauthState);
        sessionStorage.setItem(
          "postOAuthModalState",
          JSON.stringify(oauthState)
        );
        window.location.href = res.data.auth_url;
      } else {
        setLoading(false);
        showErrorToast(res?.message || "Authentication failed");
      }
    } catch (err) {
      setLoading(false);
      showErrorToast(
        err?.response?.data?.message || err?.message || "Authentication failed"
      );
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const currentToolAuthStatus = sessionStorage.getItem("toolAuth");
    console.log("Save button clicked with:", {
      selectedTool,
      selectedActions,
      isBulkAdd,
      oauthStatus: currentToolAuthStatus,
    });

    const existingTool = serverDetails?.actions?.find(
      (tool) => tool.toolId === selectedTool.toolId
    );
    const existingToolActions = existingTool?.toolActions || [];

    const actionsToAdd = selectedActions.filter(
      (selectedAction) =>
        !existingToolActions.some(
          (existingAction) =>
            existingAction.actionKey === selectedAction.actionKey
        )
    );

    try {
      // Always attempt to update toolAuth if authentication is completed and it's not already true on the backend
      if (isAuthCompleted && existingTool?.toolAuth !== "true") {
        const authUpdatePayload = {
          actions_data: {
            toolId: selectedTool.toolId,
            toolName: selectedTool.toolName,
            toolDescription: selectedTool.toolDescription,
            toolIcon: selectedTool.toolIcon,
            toolActions: [], // Empty array as we are only updating auth status
            toolAuth: "true", // Explicitly set to "true" string as per backend expectation
            authOptions: selectedTool.authOptions,
          },
        };
        console.log("Sending auth update payload:", authUpdatePayload);
        await addActionInServer(authUpdatePayload);
      }

      // In post-OAuth flow, we need to update toolAuth even if no new actions to add
      const isPostOAuthFlow = sessionStorage.getItem("toolAuth") === "true";
      
      if (actionsToAdd.length === 0 && !isPostOAuthFlow) {
        showSuccessToast(
          "All selected actions are already added, or tool authentication status updated."
        );
        setLoading(false);
        setFinalConnectModal(false);
        setSelectedActions([]);
        setSelectedTool(null);
        setIsBulkAdd(false);
        sessionStorage.removeItem("toolAuth");
        return;
      }
      
      // If post-OAuth and no actions to add, just update auth status and close
      if (actionsToAdd.length === 0 && isPostOAuthFlow) {
        showSuccessToast("Authentication completed and tool status updated successfully!");
        setLoading(false);
        setFinalConnectModal(false);
        setSelectedActions([]);
        setSelectedTool(null);
        setIsBulkAdd(false);
        sessionStorage.removeItem("toolAuth");
        return;
      }

      if (isBulkAdd) {
        const allActionsPayload = actionsToAdd.map((action) => ({
          actionName: action.actionName,
          actionDisplayName: action.actionDisplayName,
          actionDescription: action.actionDescription,
          actionType: action.actionType,
          actionKey: action.actionKey,
          actionNeeds: (action.actionNeeds || []).map((need) => ({
            type: need.type,
            value: "$$$GUESS$$$",
            required: need.required,
            param: need.param,
            description: need.description,
          })),
          isHidden: action.isHidden,
        }));

        const finalPayload = {
          actions_data: {
            toolId: selectedTool.toolId,
            toolName: selectedTool.toolName,
            toolDescription: selectedTool.toolDescription,
            toolIcon: selectedTool.toolIcon,
            toolActions: allActionsPayload,
            toolAuth: currentToolAuthStatus, // Use currentToolAuthStatus for consistency if actions are being added
            authOptions: selectedTool.authOptions,
          },
        };

        console.log("Sending bulk add payload:", finalPayload);
        await addActionInServer(finalPayload);
      } else {
        for (const action of actionsToAdd) {
          const payload = generateActionPayload(
            selectedTool,
            action,
            currentToolAuthStatus
          );
          console.log("Sending individual action payload:", {
            actions_data: payload,
          });
          await addActionInServer({ actions_data: payload });
        }
      }

      const res = await getServerById(id);
      setServerDetails(res.data);
      showSuccessToast("Actions saved successfully!");
      setFinalConnectModal(false);
      setSelectedActions([]);
      setSelectedTool(null);
      setIsBulkAdd(false);
      sessionStorage.removeItem("toolAuth");
    } catch (error) {
      showErrorToast("Error saving action(s)");
      console.error("Error saving action(s):", error);
    } finally {
      setLoading(false);
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [view, setView] = useState("tool");

  const handleToggleTool = (toolId) => {
    setFilterValue((prev) => {
      const prevTools = Array.isArray(prev?.tool) ? prev.tool : [];
      const prevActions = Array.isArray(prev?.action) ? prev.action : [];

      const isSelected = prevTools.includes(toolId);
      const updatedTools = isSelected
        ? prevTools.filter((t) => t !== toolId)
        : [...prevTools, toolId];

      // Get the actions for the selected/deselected tool
      const actionsOfThisTool =
        serverDetails.actions
          .find((tool) => tool.toolId === toolId)
          ?.toolActions.map((a) => a.actionName) || [];

      // If deselected: remove those actions; if selected: add them
      const updatedActions = isSelected
        ? prevActions.filter((id) => !actionsOfThisTool.includes(id))
        : [...new Set([...prevActions, ...actionsOfThisTool])]; // add unique only

      // Update toolActionData: only for selected tools
      const filteredToolActions = serverDetails.actions
        .filter((tool) => updatedTools.includes(tool.toolId))
        .flatMap((tool) =>
          tool.toolActions.map((action) => ({
            ...action,
            toolAuth: tool.toolAuth,
            toolIcon: tool.toolIcon,
            toolName: tool.toolName,
          }))
        );

      // Update synced action UI data
      setToolActionData({ toolActions: filteredToolActions });

      return {
        ...prev,
        tool: updatedTools,
        action: updatedActions,
      };
    });
  };

  const handleToggleAction = (actionId) => {
    setFilterValue((prev) => {
      const prevActions = Array.isArray(prev?.action) ? prev.action : [];

      const isSelected = prevActions.includes(actionId);

      // Toggle action selection
      const updatedActions = isSelected
        ? prevActions.filter((a) => a !== actionId)
        : [...prevActions, actionId];

      // Determine tools based on updated actions
      const updatedToolsSet = new Set();

      serverDetails.actions.forEach((tool) => {
        const toolActionIds = tool.toolActions.map((a) => a.actionId);
        const hasAnySelected = toolActionIds.some((id) =>
          updatedActions.includes(id)
        );
        const allUnselected = toolActionIds.every(
          (id) => !updatedActions.includes(id)
        );

        if (hasAnySelected && !allUnselected) {
          updatedToolsSet.add(tool.toolId);
        }
      });

      const updatedTools = Array.from(updatedToolsSet);

      // Update visible toolActions
      const filteredToolActions = serverDetails.actions
        .filter((tool) => updatedTools.includes(tool.toolId))
        .flatMap((tool) =>
          tool.toolActions
            .filter((action) => updatedActions.includes(action.actionId))
            .map((action) => ({
              ...action,
              toolAuth: tool.toolAuth,
              toolIcon: tool.toolIcon,
              toolName: tool.toolName,
            }))
        );

      setToolActionData({ toolActions: filteredToolActions });

      return {
        ...prev,
        action: updatedActions,
        tool: updatedTools,
      };
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Helper for dynamic icon rendering
  const renderToolIcon = (tool, size = 30) => {
    if (tool && tool.toolIcon) {
      try {
        return (
          <img
            src={require(`../static/Icons/${tool.toolIcon}`)}
            alt={tool.toolName}
            style={{
              width: size,
              height: size,
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        );
      } catch (e) {
        // fallback if icon not found
        try {
          return (
            <img
              src={require(`../static/Icons/default-tool.svg`)}
              alt="default-tool"
              style={{
                width: size,
                height: size,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          );
        } catch (e2) {
          return <Google style={{ width: size, height: size }} />;
        }
      }
    }
    // fallback if toolIcon is missing
    try {
      return (
        <img
          src={require(`../static/Icons/default-tool.svg`)}
          alt="default-tool"
          style={{
            width: size,
            height: size,
            objectFit: "contain",
            borderRadius: 8,
          }}
        />
      );
    } catch (e) {
      return <Google style={{ width: size, height: size }} />;
    }
  };

  const [anchorElIsAuthCompleted, setAnchorElIsAuthCompleted] = useState(null);

  const handleClick = (event) => {
    setAnchorElIsAuthCompleted(event.currentTarget);
  };

  const handleCloseIsAuthCompleted = () => {
    setAnchorElIsAuthCompleted(null);
  };

  const anchorElIsAuthCompletedOpen = Boolean(anchorElIsAuthCompleted);

  const filteredTools = allTools.filter(
    (app) =>
      app.toolName &&
      app.toolName.toLowerCase().includes(modalSearchTerm.toLowerCase())
  );

  const filteredActions = applicationActions.filter(
    (action) =>
      action.actionName &&
      action.actionName.toLowerCase().includes(actionsSearch.toLowerCase())
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "500px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ bgcolor: "#000", color: "white", borderRadius: 2 }}>
          {serverDetails?.actions && serverDetails.actions.length > 0 ? (
            <>
              <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", md: "row" }}
                  flexWrap="wrap"
                  gap={2}
                >
                  {/* Tabs */}
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    textColor="inherit"
                    indicatorColor="primary"
                    // variant="scrollable"
                    // scrollButtons="auto"
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      maxWidth: "100%",
                      height: { xs: "auto", md: "60px" },
                      minHeight: { xs: "48px", md: "60px" },
                      overflowX: "auto",
                      "& .MuiTab-root": {
                        minWidth: "auto",
                        flexShrink: 1,
                        whiteSpace: "nowrap",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        // padding: { xs: "6px 8px", sm: "12px 16px" },
                      },
                    }}
                  >
                    {tabData.map((tab, index) => {
                      const IconComponent = tab.icon;
                      const isSelected = selectedTab === index;

                      return (
                        <Tab
                          key={index}
                          label={tab.label}
                          icon={
                            <IconComponent
                              style={{
                                color: isSelected ? "#B9E307" : "white",
                                width: 24,
                                height: 24,
                              }}
                            />
                          }
                          iconPosition="start"
                          sx={{
                            color: isSelected ? "#B9E307" : "white",
                            fontWeight: isSelected ? "bold" : "normal",
                            flexDirection: { xs: "column", sm: "row" },
                          }}
                        />
                      );
                    })}
                  </Tabs>

                  {/* Button for Tab 0 */}
                  {selectedTab === 0 && (
                    <ButtonCommon
                      icon={<Add />}
                      sx={{
                        width: { xs: "100%", md: "max-content" },
                        mt: { xs: 1, md: 0 },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                      onClick={async () => setOpenModal(true)}
                    >
                      Add Tool
                    </ButtonCommon>
                  )}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    spacing={1}
                    width="100%"
                    mt={{ xs: 1, md: 0 }}
                    gap={{ xs: 2, sm: 0 }}
                  >
                    {/* Icons */}
                    {![0, 2, 3, 4].includes(selectedTab) && (
                      <Box
                        display="flex"
                        gap={1}
                        order={{ xs: 2, sm: 1 }}
                        width={{ xs: "100%", sm: "auto" }}
                        justifyContent={{ xs: "center", sm: "flex-start" }}
                      >
                        <Box
                          component={RefreshIcon}
                          sx={{
                            cursor: "pointer",
                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                            color:
                              JSON.stringify(filterValue) ===
                              JSON.stringify(initialFilterValue)
                                ? "#fff"
                                : "#B9E307",
                          }}
                          onClick={() => {
                            setFilterValue(initialFilterValue);
                            // Also optionally reset toolActionData to reflect that filter
                            const refreshedActions = serverDetails.actions
                              .filter((tool) =>
                                initialFilterValue.tool.includes(tool.toolId)
                              )
                              .flatMap((tool) =>
                                tool.toolActions
                                  .filter((action) =>
                                    initialFilterValue.action.includes(
                                      action.actionId
                                    )
                                  )
                                  .map((action) => ({
                                    ...action,
                                    toolAuth: tool.toolAuth,
                                    toolIcon: tool.toolIcon,
                                    toolName: tool.toolName,
                                  }))
                              );

                            setToolActionData({
                              toolActions: refreshedActions,
                            });
                          }}
                        />
                        <Box
                          component={FilterIcon}
                          sx={{
                            cursor: "pointer",
                            fontSize: { xs: "1.25rem", sm: "1.5rem" },
                            color:
                              JSON.stringify(initialFilterValue) ===
                              JSON.stringify(filterValue)
                                ? "#fff"
                                : "#B9E307",
                          }}
                          onClick={(event) => setAnchorEl(event.currentTarget)}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Container>

              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    backgroundColor: "#444343",
                    color: "#fff",
                    width: { xs: "calc(100vw - 32px)", sm: 400, md: 500 },
                    maxWidth: "95vw",
                    borderRadius: 2,
                    p: { xs: 1.5, sm: 2 },
                    mt: 2,
                  },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  }}
                >
                  Filter
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  value={view}
                  onChange={(e, newView) => newView && setView(newView)}
                  fullWidth
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#2D2D2D",
                    p: "2px",
                    mb: 2,
                    height: { xs: "40px", sm: "48px" },
                  }}
                  size="small"
                >
                  <ToggleButton
                    value="tool"
                    sx={{
                      flex: 1,
                      borderRadius: "6px !important",
                      color: "#fff",
                      backgroundColor:
                        view === "tool" ? "#1F1F1F" : "transparent",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      "&.Mui-selected": {
                        backgroundColor: "#1F1F1F",
                        color: "primary.main",
                        fontWeight: 700,
                      },
                    }}
                  >
                    Tool
                  </ToggleButton>
                  <ToggleButton
                    value="action"
                    sx={{
                      flex: 1,
                      borderRadius: "6px !important",
                      color: "#fff",
                      backgroundColor:
                        view === "action" ? "#171616" : "transparent",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      "&.Mui-selected": {
                        backgroundColor: "#171616",
                        color: "primary.main",
                        fontWeight: 700,
                      },
                    }}
                  >
                    Action
                  </ToggleButton>
                </ToggleButtonGroup>

                <TextField
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      color: "#fff",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    },
                    "& .MuiOutlinedInput-root": {
                      height: { xs: "40px", sm: "48px" },
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#888" },
                    },
                  }}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  value={searchFilter || ""}
                />

                {view === "tool" ? (
                  <List
                    dense
                    sx={{
                      maxHeight: { xs: 150, sm: 200 },
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      "& .MuiListItem-root": {
                        py: { xs: 0.5, sm: 1 },
                      },
                    }}
                  >
                    {serverDetails.actions
                      .filter(
                        (tool) =>
                          searchFilter
                            ? (tool?.toolName || "")
                                .toLowerCase()
                                .includes(searchFilter?.toLowerCase())
                            : true // Show all tools when searchFilter is empty
                      )
                      .map((tool, i) => (
                        <ListItem key={i} disablePadding>
                          <ListItemIcon sx={{ minWidth: { xs: 40, sm: 56 } }}>
                            <Checkbox
                              checked={filterValue?.tool?.includes(tool.toolId)}
                              onChange={() => handleToggleTool(tool.toolId)}
                              size={
                                window.innerWidth < 600 ? "small" : "medium"
                              }
                              sx={{
                                color: "#c6ff00",
                                "&.Mui-checked": { color: "#c6ff00" },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: { xs: 0.5, sm: 1 },
                                }}
                              >
                                {renderToolIcon(tool, 30)}
                                <Typography
                                  sx={{
                                    textTransform: "capitalize",
                                    color: "#fff",
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {tool.toolName}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                  </List>
                ) : (
                  <List
                    dense
                    sx={{
                      maxHeight: { xs: 150, sm: 200 },
                      overflowY: "scroll",
                      scrollbarWidth: "none",
                      "& .MuiListItem-root": {
                        py: { xs: 0.5, sm: 1 },
                      },
                    }}
                  >
                    {serverDetails.actions.flatMap((tool) =>
                      tool.toolActions
                        .filter(
                          (action) =>
                            searchFilter
                              ? action?.actionDisplayName
                                  ?.toLowerCase()
                                  .includes(searchFilter?.toLowerCase())
                              : true // ✅ Show all if search is empty
                        )
                        .map((action, j) => (
                          <ListItem key={`${tool.toolId}-${j}`} disablePadding>
                            <ListItemIcon sx={{ minWidth: { xs: 40, sm: 56 } }}>
                              <Checkbox
                                checked={filterValue?.action?.includes(
                                  action.actionId
                                )}
                                onChange={() =>
                                  handleToggleAction(action.actionId)
                                }
                                size={
                                  window.innerWidth < 600 ? "small" : "medium"
                                }
                                sx={{
                                  color: "#c6ff00",
                                  "&.Mui-checked": { color: "#c6ff00" },
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: { xs: 0.5, sm: 1 },
                                  }}
                                >
                                  {renderToolIcon(tool, 30)}
                                  <Typography
                                    sx={{
                                      textTransform: "capitalize",
                                      color: "#fff",
                                      fontSize: { xs: "0.875rem", sm: "1rem" },
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {action.actionDisplayName}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))
                    )}
                  </List>
                )}
              </Popover>

              <Box
                sx={{
                  mt: { xs: 2, sm: 3 },
                  overflowY: "scroll",
                  scrollbarWidth: "none",
                  maxHeight: {
                    xs: `calc(${divHeight - 180}px)`,
                    sm: `calc(${divHeight - 200}px)`,
                    md: `calc(${divHeight - 210}px)`,
                  },
                  px: { xs: 1, sm: 0 },
                }}
              >
                {selectedTab === 0 && (
                  <ToolsPage
                    serverDetails={serverDetails}
                    setServerDetails={setServerDetails}
                    setSelectedTab={setSelectedTab}
                    setToolActionData={setToolActionData}
                    setFilterValue={setFilterValue}
                    setInitialFilterValue={setInitialFilterValue}
                    setOpenModal={setOpenModal}
                  />
                )}
                {selectedTab === 1 && (
                  <ActionPage
                    setServerDetails={setServerDetails}
                    toolActionData={toolActionData}
                    setSelectedTab={setSelectedTab}
                  />
                )}
                {selectedTab === 2 && (
                  <TestPage serverDetails={serverDetails} />
                )}
                {selectedTab === 3 && <ConnectPage />}
                {selectedTab === 4 && <HistoryPage />}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 2, sm: 3, md: 4 },
                width: "100%",
                height: {
                  xs: `calc(${divHeight - 180}px)`,
                  sm: `calc(${divHeight - 200}px)`,
                  md: `calc(${divHeight - 210}px)`,
                },
                minHeight: { xs: "300px", sm: "400px" },
              }}
            >
              <img
                src={Logo}
                style={{
                  width: window.innerWidth < 600 ? "60px" : "80px",
                  marginBottom: "16px",
                }}
                alt="mcp-logo"
              />
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  mb: { xs: 1, sm: 2 },
                  color: "#F4F4F4",
                  fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
                  px: { xs: 1, sm: 0 },
                }}
              >
                No Tools Here
              </Typography>
              <Typography
                variant="body1"
                color="white"
                sx={{
                  mb: { xs: 2, sm: 3 },
                  maxWidth: { xs: 300, sm: 400, md: 450 },
                  mx: "auto",
                  color: "#AEAEAE",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: { xs: 1.4, sm: 1.5 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                Add Your First Tool to Start Connecting your AI Assistants to
                thousands of apps on MCP.Cloud
              </Typography>
              <ButtonCommon
                icon={<AddIcon />}
                onClick={() => setOpenModal(true)}
                sx={{
                  width: { xs: "100%", sm: "max-content" },
                  maxWidth: { xs: "280px", sm: "none" },
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  py: { xs: 1.5, sm: 1 },
                  px: { xs: 3, sm: 2 },
                }}
              >
                Add Tool
              </ButtonCommon>
            </Box>
          )}
        </Paper>
      )}

      {/* Add Tools Modal */}
      <CommonModal
        open={openModal}
        handleClose={() => {
          setOpenModal(false);
          setSelectedApplication(null);
          setApplicationActions([]);
          setSelectedActions([]);
          setModalSearchTerm("");
          setActionSearch("");
        }}
        title="Select a Tool"
        submitText="Next"
        submitProps={{
          disabled: selectedActions.length === 0,
          onClick: () => {
            setOpenModal(false);
            setFinalConnectModal(true);
            const allSelected =
              applicationActions.length > 0 &&
              selectedActions.length === applicationActions.length;
            setIsBulkAdd(allSelected);
          },
        }}
        cancelText="Back"
        cancelProps={{
          onClick: () => {
            setOpenModal(false);
            setSelectedApplication(null);
            setApplicationActions([]);
            setSelectedActions([]);
            setModalSearchTerm("");
            setActionSearch("");
          },
        }}
        dialogProps={{
          PaperProps: {
            sx: {
              bgcolor: "#171616",
              color: "white",
              borderRadius: "12px",
              width: { xs: "95vw", sm: "700px", md: "800px" },
              maxWidth: { xs: "95vw", sm: "700px", md: "800px" },
              height: { xs: "80vh", sm: "600px", md: "600px" },
              maxHeight: { xs: "80vh", sm: "600px", md: "600px" },
            },
          },
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          height="100%"
        >
          {/* Left Panel: Applications List */}
          <Box
            sx={{
              width: { xs: "100%", md: "35%" },
              height: { xs: "50%", md: "100%" },
              display: "flex",
              flexDirection: "column",
              py: 2,
              borderRight: { xs: "none", md: "1px solid #333" },
              borderBottom: { xs: "1px solid #333", md: "none" },
            }}
          >
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "#fff", fontWeight: "bold" }}
              >
                Tools
              </Typography>
              <TextField
                variant="outlined"
                placeholder="Search tools..."
                fullWidth
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#2C2C2C",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#555",
                    },
                    input: {
                      color: "#fff",
                    },
                  },
                }}
                size="small"
              />
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                maxHeight: {
                  xs: "200px",
                  sm: "300px",
                  md: "100%",
                },
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },
              }}
            >
              <List>
                {loadingTools ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  filteredTools.map((tool) => (
                    <ListItemButton
                      key={tool.toolId}
                      selected={selectedApplication?.toolId === tool.toolId}
                      onClick={() => handleSelectApplication(tool)}
                      sx={{
                        borderRadius: "8px",
                        mb: 0.5,
                        backgroundColor:
                          selectedApplication?.toolId === tool.toolId
                            ? "#2C2C2C"
                            : "transparent",
                        "&:hover": {
                          backgroundColor: "#2C2C2C",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {renderToolIcon(tool, 24)}
                      </ListItemIcon>
                      <ListItemText
                        primary={tool.toolName}
                        sx={{ textTransform: "capitalize", color: "#fff" }}
                      />
                    </ListItemButton>
                  ))
                )}
              </List>
            </Box>
          </Box>

          {/* Right Panel: Actions for Selected Application */}
          <Box
            sx={{
              width: { xs: "100%", md: "65%" },
              height: { xs: "50%", md: "100%" },
              pl: { xs: 2, md: 3 },
              pt: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "#fff", fontWeight: "bold" }}
              >
                Actions
              </Typography>
              <TextField
                variant="outlined"
                placeholder="Search Actions..."
                fullWidth
                value={actionsSearch}
                onChange={(e) => setActionSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9e9e9e" }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#2C2C2C",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#555",
                    },
                    input: {
                      color: "#fff",
                    },
                  },
                }}
                size="small"
              />
            </Box>
            {loadingApplicationActions ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                <CircularProgress />
              </Box>
            ) : selectedApplication ? (
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflowY: "auto",
                  minHeight: 0,
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                }}
              >
                {applicationActions && applicationActions.length > 0 ? (
                  <>
                    <Box
                      display="flex"
                      alignItems="center"
                      mb={1}
                      sx={{
                        background: "#333",
                        padding: "10px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedActions(applicationActions)}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AddCircleOutlineIcon sx={{ color: "#fff" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`Add all ${selectedApplication?.toolName} actions`}
                        sx={{ color: "#fff" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        overflowY: "auto",
                        maxHeight: {
                          xs: "200px",
                          sm: "300px",
                          md: "400px",
                        },
                        "&::-webkit-scrollbar": {
                          width: "8px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          backgroundColor: "#555",
                        },
                      }}
                    >
                      <List>
                        {filteredActions.map((action) => (
                          <ListItem
                            key={action.actionKey} // Using actionKey
                            dense
                            disablePadding
                            sx={{
                              background: selectedActions.find(
                                (a) => a.actionKey === action.actionKey // Using actionKey
                              )
                                ? "#555"
                                : "#333",
                              padding: "10px",
                              borderRadius: "8px",
                              mb: 1,
                              cursor: "pointer",
                            }}
                            onClick={() => handleToggleActionSelection(action)}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {renderToolIcon(selectedApplication, 24)}
                            </ListItemIcon>
                            <ListItemText
                              primary={action.actionDisplayName}
                              sx={{
                                textTransform: "capitalize",
                                color: "#fff",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="400px"
                    width="100%"
                  >
                    <Typography color="#9E9E9E">
                      No actions available for this tool.
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100%"
                textAlign="center"
                color="#A0A0A0"
              >
                <ToolsIcon
                  style={{
                    width: 50,
                    height: 50,
                    marginBottom: 16,
                    color: "#D0D0D0",
                  }}
                />
                <Typography variant="h6" sx={{ color: "#555" }}>
                  Select a tool from the sidebar
                </Typography>
                <Typography variant="body2">to view available tools</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CommonModal>

      {/* Final Connect Modal */}
      <CommonModal
        open={finalConnectModal}
        handleClose={() => {
          setFinalConnectModal(false);
          setSelectedActions([]);
          setSelectedTool(null);
        }}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <IconButton
              sx={{ color: "#ECECEC" }}
              onClick={() => {
                setFinalConnectModal(false);
                setOpenModal(true);
              }}
            >
              <BackIcon />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 30,
                bgcolor: "#00000038",
                p: 0.7,
                borderRadius: 1,
              }}
            >
              {renderToolIcon(selectedTool, 30)}
            </Box>
            <Typography sx={{ color: "white", textTransform: "capitalize" }}>
              {selectedTool?.toolName}
            </Typography>
          </Box>
        }
        cancelText="Cancel"
        cancelProps={{
          onClick: () => {
            setFinalConnectModal(false);
          },
        }}
        submitText="Save"
        submitProps={{
          sx: {
            "&.Mui-disabled": {
              color: "#888",
              borderColor: "#555",
              backgroundColor: "#222",
            },
          },
          children: loading ? (
            <CircularProgress size={16} />
          ) : isBulkAdd ? (
            `Add all ${selectedTool?.toolName} actions`
          ) : (
            "Save"
          ),
          loading: loading,

          disabled: !isAuthCompleted,
          onClick: async () => handleSave(),
        }}
      >
        <Box variant="outlined">
          <Box>
            <Typography
              sx={{ color: "#ECECEC", mb: 1, textTransform: "capitalize" }}
            >
              {selectedTool?.toolName} account
              <span style={{ color: "red" }}>*</span>
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Box
                key={selectedActions?.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0px",
                  padding: "10px",
                  cursor: "pointer",
                  background: "#2C2F3366",
                  border: "1px solid #50505030",
                  borderRadius: 2,
                }}
              >
                <ListItemIcon>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 30,
                      height: 30,
                      bgcolor: "#2C2F3366",
                      borderRadius: 1,
                      p: 0.7,
                    }}
                  >
                    {/* <Box component={Google} /> */}
                    {renderToolIcon(selectedTool, 30)}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={selectedTool?.toolName}
                  sx={{
                    margin: 0,
                    "& .MuiTypography-root": {
                      color: "white",
                      fontSize: "0.95rem",
                      fontWeight: 400,
                    },
                    textTransform: "capitalize",
                  }}
                />
                {!isAuthCompleted ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      width: "100%",
                      maxWidth: 300,
                    }}
                  >
                    {/* Dropdown */}
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiInputLabel-root": {
                          color: "white",
                        },
                        "& .MuiOutlinedInput-root": {
                          color: "white",
                          "& fieldset": {
                            borderColor: "primary.main",
                          },
                          "&:hover fieldset": {
                            borderColor: "primary.dark",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        "& .MuiSvgIcon-root": {
                          color: "white",
                        },
                      }}
                    >
                      <InputLabel id="auth-method-label">
                        Auth Option
                      </InputLabel>
                      <Select
                        labelId="auth-method-label"
                        value={oauthMethod}
                        label="Auth Option"
                        onChange={handleChange}
                      >
                        {selectedTool?.authOptions.map((item) => (
                          <MenuItem key={item} value={item}>
                            {formatLabel(item)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleClick}
                      sx={{ color: "#fff", borderColor: "#fff" }}
                    >
                      Connect New Account
                    </Button>

                    <Popover
                      id={id}
                      open={anchorElIsAuthCompletedOpen}
                      anchorEl={anchorElIsAuthCompleted}
                      onClose={handleCloseIsAuthCompleted}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      PaperProps={{
                        sx: {
                          backgroundColor: "#333",
                          color: "#fff",
                          p: 2,
                          borderRadius: 2,
                        },
                      }}
                    >
                      {selectedTool?.authOptions?.length > 0 ? (
                        selectedTool.authOptions.map((item) => (
                          <Box
                            key={item}
                            onClick={(e) => {
                              handleChange({ target: { value: item } });
                              handleClose();
                            }}
                            sx={{
                              px: 1,
                              py: 0.5,
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "#444",
                              },
                            }}
                          >
                            {formatLabel(item)}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2">
                          No options available
                        </Typography>
                      )}
                    </Popover>
                  </>
                )}
              </Box>
              <Box>
                {oauthMethod === "personal_access_token" && !isAuthCompleted && (
                  <Box display={"flex"} alignItems={"center"} gap={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Enter Personal Access Token"
                      variant="outlined"
                      value={personalAccessToken}
                      onChange={(e) => {
                        setPersonalAccessToken(e.target.value);
                        if (tokenError) setTokenError("");
                      }}
                      // onBlur={handleTokenSubmit}
                      error={!!tokenError}
                      helperText={tokenError}
                      sx={{
                        input: { color: "white" },
                        "& label": { color: "white" },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "primary.main" },
                          "&:hover fieldset": {
                            borderColor: "primary.dark",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                      }}
                    />
                    <ButtonCommon
                      variant="contained"
                      sx={{
                        width: "fit-content",
                        height: "fit-content",
                        p: 0,
                        px: 1,
                      }}
                      onClick={handleTokenSubmit}
                      disabled={loading || isAuthCompleted}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : isAuthCompleted ? (
                        "Connection successful"
                      ) : (
                        "Connect"
                      )}
                    </ButtonCommon>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </CommonModal>
    </>
  );
};

export default ServerDetails;
